import { Result, Search } from "core/types/search.types";
import { FunctionComponent, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import ThumbUpAltRoundedIcon from "@mui/icons-material/ThumbUpAltRounded";
import ThumbDownAltRoundedIcon from "@mui/icons-material/ThumbDownAltRounded";
import RemoveCircleOutlineRoundedIcon from "@mui/icons-material/RemoveCircleOutlineRounded";
import PendingRoundedIcon from "@mui/icons-material/PendingRounded";
import AllInclusiveRoundedIcon from "@mui/icons-material/AllInclusiveRounded";
import {
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Tooltip,
  IconButton,
  TableHead,
  Checkbox,
  Box,
  Slider,
} from "@mui/material";
import { Text } from "components/atoms/text";
import InsertLinkRoundedIcon from "@mui/icons-material/InsertLinkRounded";
import { LoadingButton } from "@mui/lab";
import { grey } from "@mui/material/colors";

interface Props {
  search: Search;
  detailMode?: boolean;
  style?: React.CSSProperties;
  isPracticeLoading: boolean;
  generatePractice: (urls: Result[]) => void;
}

const ResearchTable: FunctionComponent<Props> = ({
  search,
  generatePractice,
  style,
  isPracticeLoading,
  detailMode = false,
}) => {
  const { t } = useTranslation();
  const [searchList, setSearchList] = useState<Result[]>([]);

  // FILTRI
  const [sentimentFilter, setSentimentFilter] = useState<string>("all");
  const [confidenceRange, setConfidenceRange] = useState<number[]>([0, 100]);

  const handleAddNewSearch = (newItem: Result) => {
    setSearchList((prev) =>
      prev.find((sc) => sc.link === newItem.link)
        ? prev.filter((sc) => sc.link !== newItem.link)
        : [...prev, newItem]
    );
  };

  const toggleSentimentFilter = (value: string) => {
    setSentimentFilter((prev) => (prev === value ? "all" : value));
  };

  const filteredResults = useMemo(() => {
    return search.results.filter((r) => {
      const sentimentValue = r.sentiment?.sentiment ?? "in_caricamento";
      const confidenceValue = r.sentiment?.confidence_subject_match ?? 0;

      const sentimentMatch =
        sentimentFilter === "all" ? true : sentimentValue === sentimentFilter;

      const confidenceMatch =
        confidenceValue >= confidenceRange[0] &&
        confidenceValue <= confidenceRange[1];

      return sentimentMatch && confidenceMatch;
    });
  }, [search.results, sentimentFilter, confidenceRange]);

  // SELEZIONA TUTTO: opera sui risultati FILTRATI (confidence + sentiment), esclusi i gia' segnalati
  // (checkbox disabilitata). Cosi' su 400 link: imposti la confidence, poi 1 click invece di 400.
  const selectableFiltered = useMemo(
    () => filteredResults.filter((r) => !r.signaled),
    [filteredResults]
  );
  const allFilteredSelected =
    selectableFiltered.length > 0 &&
    selectableFiltered.every((r) => searchList.some((sc) => sc.link === r.link));
  const someFilteredSelected = selectableFiltered.some((r) =>
    searchList.some((sc) => sc.link === r.link)
  );
  const toggleSelectAllFiltered = () => {
    setSearchList((prev) => {
      if (allFilteredSelected) {
        const links = new Set(selectableFiltered.map((r) => r.link));
        return prev.filter((sc) => !links.has(sc.link)); // deseleziona i filtrati
      }
      const have = new Set(prev.map((sc) => sc.link));
      return [...prev, ...selectableFiltered.filter((r) => !have.has(r.link))]; // aggiunge i mancanti (dedup)
    });
  };

  const loadingCount = search.results.filter((r) => !r.sentiment).length;

  const confidenceFiltered = useMemo(() => {
    return search.results.filter((r) => {
      const confidenceValue = r.sentiment?.confidence_subject_match ?? 0;
      return (
        confidenceValue >= confidenceRange[0] &&
        confidenceValue <= confidenceRange[1]
      );
    });
  }, [search.results, confidenceRange]);

  const stats = confidenceFiltered.reduce(
    (acc, r) => {
      if (!r.sentiment) {
        acc.loading++;
        return acc;
      }

      if (r.sentiment.sentiment === "Positive") acc.positive++;
      if (r.sentiment.sentiment === "Negative") acc.negative++;
      if (r.sentiment.sentiment === "Neutral") acc.neutral++;

      return acc;
    },
    { positive: 0, negative: 0, neutral: 0, loading: 0 }
  );

  const StatBox = ({
    icon,
    label,
    value,
    filter,
  }: {
    icon: JSX.Element;
    label: string;
    value: number;
    filter: string;
  }) => (
    <Box
      onClick={() => toggleSentimentFilter(filter)}
      sx={{
        cursor: "pointer",
        border:
          sentimentFilter === filter
            ? "2px solid #1976d2"
            : "2px solid transparent",
        bgcolor: grey[100],
      }}
      display="flex"
      alignItems="center"
      gap={1}
      px={2}
      py={1}
      borderRadius={2}
    >
      {icon}
      <Text fontSize="14px">
        {value} {label}
      </Text>
    </Box>
  );

  return (
    <>
      {/* FILTRI */}
      <Box display="flex" gap={3} mb={3} alignItems="center">
        <Box width={260}>
          <Text fontSize="13px" mb={1}>
            Confidence (0–100%)
          </Text>
          <Slider
            value={confidenceRange}
            onChange={(_, v) => setConfidenceRange(v as number[])}
            valueLabelDisplay="auto"
            step={1}
            min={0}
            max={100}
          />
        </Box>

        <LoadingButton
          variant="outlined"
          sx={{ ml: "auto" }}
          disabled={selectableFiltered.length === 0}
          onClick={toggleSelectAllFiltered}
        >
          {allFilteredSelected
            ? "Deseleziona tutto"
            : `Seleziona tutto (${selectableFiltered.length})`}
        </LoadingButton>

        <LoadingButton
          variant="contained"
          disabled={searchList.length === 0}
          loading={isPracticeLoading}
          onClick={() => generatePractice(searchList)}
        >
          {t("report.generate_practice")} ({searchList.length})
        </LoadingButton>
      </Box>

      {/* STATISTICHE */}
      <Box mb={3}>
        <Text fontSize="16px" fontWeight="700" mb={1}>
          Risultati filtrati: {filteredResults.length} / {search.results.length}
        </Text>

        <Box display="flex" gap={2} flexWrap="wrap">
          {/* ALL */}
          <StatBox
            icon={<AllInclusiveRoundedIcon sx={{ fontSize: 20 }} />}
            label="All"
            value={search.results.length}
            filter="all"
          />

          <StatBox
            icon={<ThumbUpAltRoundedIcon color="success" sx={{ fontSize: 20 }} />}
            label="Positive"
            value={stats.positive}
            filter="Positive"
          />

          <StatBox
            icon={
              <RemoveCircleOutlineRoundedIcon color="info" sx={{ fontSize: 20 }} />
            }
            label="Neutral"
            value={stats.neutral}
            filter="Neutral"
          />

          <StatBox
            icon={<ThumbDownAltRoundedIcon color="error" sx={{ fontSize: 20 }} />}
            label="Negative"
            value={stats.negative}
            filter="Negative"
          />

          <StatBox
            icon={<PendingRoundedIcon color="disabled" sx={{ fontSize: 20 }} />}
            label="In loading"
            value={loadingCount}
            filter="in_caricamento"
          />
        </Box>
      </Box>

      {/* TABELLA */}
      <TableContainer
        component={Paper}
        style={{ position: "relative", boxShadow: "unset", ...style }}
      >
        <Table>
          {detailMode && (
            <TableHead>
              <TableRow>
                <TableCell>Thumbnail</TableCell>

                <Tooltip title="Titolo della pagina risultante dalla ricerca">
                  <TableCell>Titolo</TableCell>
                </Tooltip>

                <Tooltip title="Riassunto o descrizione del contenuto analizzato">
                  <TableCell>Descrizione</TableCell>
                </Tooltip>

                <Tooltip title="Valutazione del sentiment del contenuto">
                  <TableCell>Sentiment</TableCell>
                </Tooltip>

                <Tooltip title="Percentuale di rilevanza rispetto al tema">
                  <TableCell>Livello di incidenza</TableCell>
                </Tooltip>

                <TableCell />
                <TableCell padding="checkbox">
                  <Tooltip title="Seleziona/deseleziona tutti i risultati filtrati">
                    <Checkbox
                      checked={allFilteredSelected}
                      indeterminate={!allFilteredSelected && someFilteredSelected}
                      onChange={toggleSelectAllFiltered}
                      disabled={selectableFiltered.length === 0}
                    />
                  </Tooltip>
                </TableCell>
              </TableRow>
            </TableHead>
          )}

          <TableBody>
            {filteredResults.map((result, index) => (
              <TableRow
                key={index}
                sx={{
                  background: result.signaled ? grey[200] : "transparent",
                }}
              >
                <TableCell>
                  {result.thumbnail && (
                    <img
                      src={result.thumbnail}
                      alt={result.title}
                      style={{
                        width: 92,
                        height: 88,
                        borderRadius: 5,
                        objectFit: "cover",
                      }}
                    />
                  )}
                </TableCell>

                <TableCell>{result.title}</TableCell>

                <TableCell>
                  {result.sentiment
                    ? result.sentiment.summary
                    : result.description}
                </TableCell>

                <TableCell>
                  {result.sentiment?.sentiment ?? "In caricamento..."}
                </TableCell>

                <TableCell>
                  {result.sentiment
                    ? result.sentiment.confidence_subject_match + "%"
                    : "..."}
                </TableCell>

                <TableCell>
                  <IconButton href={result.link} target="_blank">
                    <InsertLinkRoundedIcon color="info" />
                  </IconButton>
                </TableCell>

                <TableCell>
                  <Checkbox
                    checked={!!searchList.find((sc) => sc.link === result.link)}
                    onChange={() => handleAddNewSearch(result)}
                    disabled={result.signaled}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default ResearchTable;
